/**
 * Active Rentals Dashboard - Frontend JavaScript
 * Displays customer's active book rentals and rental information
 */

// ============================================================================
// BOOK RENTAL SYSTEM
// ============================================================================
// CONFIGURATION - Reads from global config set in theme.liquid
const API_BASE_URL = window.__BOOKBUGS_CONFIG?.API_BASE_URL || 'https://order-return-testing-cjlu.onrender.com/api';
// ============================================================================

// Book Rental Application for Shopify Storefront
(function() {
  'use strict';

  // Prevent duplicate class declaration
  if (typeof window.BookRentalApp === 'undefined') {
    window.BookRentalApp = class BookRentalApp {
      constructor() {
        this.customerId = null;
        this.customerPlan = null;
        this.planName = null;
        this.rentalLimit = 5;
        this.activeRentals = []; // CRITICAL: Must always be an array
        this.apiBaseUrl = API_BASE_URL;
        
        // CRITICAL: Ensure activeRentals is always an array
        if (!Array.isArray(this.activeRentals)) {
          console.error('CRITICAL: activeRentals not initialized as array in constructor');
          this.activeRentals = [];
        }
        
        this.init();
      }

      async init() {
        try {
          // Check authentication status first
          const authStatus = this.checkAuthentication();
          if (!authStatus.isAuthenticated) {
            this.showAuthError(authStatus.message, authStatus.type);
            return;
          }

          this.customerId = this.getCustomerId();
          console.log('✅ Using customer ID:', this.customerId);
          await this.loadInitialData();
          this.setupEventListeners();
        } catch (error) {
          console.error('Initialization error:', error);
          this.showError(error.message || 'An unexpected error occurred. Please try refreshing the page.');
        }
      }

      checkAuthentication() {
        const container = document.querySelector('.book-rental-container');
        if (!container) {
          return { isAuthenticated: false, message: 'Page configuration error', type: 'error' };
        }

        const customerId = container.dataset.customerId;
        const noAuth = container.dataset.noAuth;
        const noPlan = container.dataset.noPlan;

        if (noAuth === 'true' || customerId === 'guest') {
          return { 
            isAuthenticated: false, 
            message: 'You need to be logged in to access your rental dashboard.', 
            type: 'no-auth' 
          };
        }

        if (noPlan === 'true') {
          return { 
            isAuthenticated: false, 
            message: 'You need an active subscription plan to access the rental dashboard. Please contact support to set up your plan.', 
            type: 'no-plan' 
          };
        }

        if (!customerId || customerId === 'demo-customer') {
          return { 
            isAuthenticated: false, 
            message: 'Authentication error. Please log in again.', 
            type: 'error' 
          };
        }

        return { isAuthenticated: true, message: '', type: '' };
      }

      getCustomerId() {
        const container = document.querySelector('.book-rental-container');
        const customerId = container ? container.dataset.customerId : null;
        console.log('Customer ID:', customerId);
        return customerId;
      }

      async loadInitialData() {
        console.log('Starting loadInitialData...');
        try {
          // Always reset UI to loading state to prevent showing stale data
          const loadingState = document.getElementById('loading-state');
          const mainContent = document.getElementById('main-content');
          const errorState = document.getElementById('error-state');
          if (mainContent) mainContent.style.display = 'none';
          if (errorState) errorState.style.display = 'none';
          if (loadingState) {
            loadingState.style.display = 'block';
            loadingState.innerHTML = `
              <div class="loading-emoji">📚</div>
              <p>Loading your rental dashboard...</p>
              <p style="font-size: 0.9rem; color: #333333; margin-top: 0.5rem;">Fetching your rental information...</p>
            `;
          }

          // Fetch plan and rentals data - NO DEMO FALLBACKS
          let planData = null;
          let rentalsData = [];
          
          try {
            console.log('🔄 Starting plan and rentals fetch for customer:', this.customerId);
            
            // Add timeout for plan and rentals fetch
            const planRentalsPromise = Promise.all([
              this.fetchCustomerPlan(),
              this.fetchActiveRentals()
            ]);
            
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Plan and rentals fetch timed out')), 15000);
            });
            
            const [planResponse, rentalsResponse] = await Promise.race([planRentalsPromise, timeoutPromise]);
            console.log('✅ Plan and rentals fetch completed');
            planData = planResponse;
            rentalsData = rentalsResponse;
          } catch (apiError) {
            console.error('❌ API fetch failed:', apiError);
            this.showError(`Failed to load your rental data: ${apiError.message}`);
            return;
          }

          // Validate plan data
          if (!planData || !planData.plan) {
            console.error('❌ Invalid plan data received:', planData);
            this.showError('Failed to load your subscription plan. Please refresh the page.');
            return;
          }
          
          this.customerPlan = planData.plan;
          this.planName = planData.planName;
          this.rentalLimit = planData.limit;
          
          console.log('📋 Plan loaded:', { plan: this.customerPlan, name: this.planName, limit: this.rentalLimit });
          
          // Ensure activeRentals is always an array
          this.activeRentals = Array.isArray(rentalsData) ? rentalsData : [];
          if (!Array.isArray(rentalsData)) {
            console.error('❌ rentalsData is not an array:', rentalsData);
            this.activeRentals = [];
          }
          
          console.log('📚 Active rentals loaded:', this.activeRentals.length, 'rentals');
          
        } catch (error) {
          console.error('❌ Unexpected error in loadInitialData:', error);
          this.showError(`Failed to load your rental dashboard: ${error.message}`);
          return;
        }

        console.log('Rendering components...');
        this.updatePlanDisplay();
        this.updateRentalSummary();
        this.renderActiveRentals();
        
        // Always show main content after loading is complete
        console.log('Showing main content...');
        this.showMainContent();
      }

      async fetchCustomerPlan() {
        console.log('Fetching customer plan...');
        try {
          const response = await fetch(`${this.apiBaseUrl}/plan?customer_id=${this.customerId}`, { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const data = await response.json();
          console.log('✅ Customer plan fetched:', data);
          return data;
        } catch (error) {
          console.error('❌ Error fetching customer plan:', error);
          throw error;
        }
      }

      async fetchActiveRentals() {
        console.log('Fetching active rentals...');
        try {
          const response = await fetch(`${this.apiBaseUrl}/returns?customer_id=${this.customerId}`, { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          const data = await response.json();
          console.log('✅ Active rentals fetched:', data);
          return data.active || [];
        } catch (error) {
          console.error('❌ Error fetching active rentals:', error);
          throw error;
        }
      }

      updatePlanDisplay() {
        const planDisplay = document.getElementById('plan-display');
        if (planDisplay && this.planName) {
          planDisplay.textContent = this.planName;
        }
      }

      updateRentalSummary() {
        const activeRentalsCount = document.getElementById('active-rentals-count');
        const rentalLimit = document.getElementById('rental-limit');
        const availableSlots = document.getElementById('available-slots');

        if (activeRentalsCount) {
          activeRentalsCount.textContent = this.activeRentals.length;
        }

        if (rentalLimit) {
          rentalLimit.textContent = this.rentalLimit;
        }

        if (availableSlots) {
          const available = Math.max(0, this.rentalLimit - this.activeRentals.length);
          availableSlots.textContent = available;
        }
      }

      renderActiveRentals() {
        const activeRentalsContainer = document.getElementById('active-rentals');
        const emptyState = document.getElementById('empty-rentals-state');

        if (!activeRentalsContainer) {
          console.error('Active rentals container not found');
          return;
        }

        if (this.activeRentals.length === 0) {
          // Show empty state
          if (activeRentalsContainer) {
            activeRentalsContainer.style.display = 'none';
          }
          if (emptyState) {
            emptyState.style.display = 'block';
          }
          return;
        }

        // Hide empty state and show rentals
        if (emptyState) {
          emptyState.style.display = 'none';
        }
        if (activeRentalsContainer) {
          activeRentalsContainer.style.display = 'grid';
        }

        // Render each rental
        const rentalHTML = this.activeRentals.map((rental, index) => {
          return this.createRentalCard(rental, index);
        }).join('');

        activeRentalsContainer.innerHTML = rentalHTML;
      }

      createRentalCard(rental, index) {
        const rentalDate = new Date(rental.rented_at || rental.created_at);
        const formattedDate = rentalDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        // Calculate rental duration
        const now = new Date();
        const durationMs = now - rentalDate;
        const durationDays = Math.floor(durationMs / (1000 * 60 * 60 * 24));
        const durationText = durationDays === 0 ? 'Today' : 
                            durationDays === 1 ? '1 day ago' : 
                            `${durationDays} days ago`;

        return `
          <div class="active-rental-card">
            <div class="rental-status">Active</div>
            <div class="book-image-container">
              ${rental.image_url ? 
                `<img src="${rental.image_url}" alt="${rental.title}" class="book-image">` :
                `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #333333; font-size: 3rem;">📚</div>`
              }
            </div>
            <h3 class="book-title">${rental.title || 'Unknown Book'}</h3>
            <p class="rental-date">Rented: ${formattedDate}</p>
            <p class="rental-duration">${durationText}</p>
          </div>
        `;
      }

      showMainContent() {
        const loadingState = document.getElementById('loading-state');
        const mainContent = document.getElementById('main-content');

        if (loadingState) {
          loadingState.style.display = 'none';
        }

        if (mainContent) {
          mainContent.style.display = 'block';
        }
      }

      showAuthError(message, type) {
        console.error('🔒 Authentication Error:', message);
        
        // Hide loading and main content
        const loadingState = document.getElementById('loading-state');
        const mainContent = document.getElementById('main-content');
        const authErrorState = document.getElementById('auth-error-state');
        const errorMessage = document.getElementById('auth-error-message');

        if (loadingState) loadingState.style.display = 'none';
        if (mainContent) mainContent.style.display = 'none';
        
        if (authErrorState) {
          authErrorState.style.display = 'block';
          
          // Update error message based on type
          if (errorMessage) {
            if (type === 'no-plan') {
              errorMessage.textContent = 'You need an active subscription plan to access the rental dashboard. Please contact support to set up your plan.';
            } else if (type === 'no-auth') {
              errorMessage.textContent = 'You need to be logged in to access your rental dashboard.';
            } else {
              errorMessage.textContent = message;
            }
          }
        }
      }

      showError(message) {
        const loadingState = document.getElementById('loading-state');
        const mainContent = document.getElementById('main-content');
        const errorState = document.getElementById('error-state');
        const errorMessage = document.getElementById('error-message');

        if (loadingState) loadingState.style.display = 'none';
        if (mainContent) mainContent.style.display = 'none';
        if (errorState) errorState.style.display = 'block';
        if (errorMessage) errorMessage.textContent = message;
      }

      setupEventListeners() {
        // No event listeners needed for read-only dashboard
        console.log('✅ Event listeners setup complete (read-only dashboard)');
      }
    };
  }

  // Initialize the app when DOM is ready
  let bookRentalAppInstance = null;
  document.addEventListener('DOMContentLoaded', function() {
    console.log('🚀 Initializing Active Rentals Dashboard...');
    bookRentalAppInstance = new window.BookRentalApp();
  });

  // Re-fetch fresh data when navigating back (bfcache restoration)
  window.addEventListener('pageshow', function(event) {
    if (event.persisted && bookRentalAppInstance) {
      console.log('🔄 Page restored from bfcache, re-fetching data...');
      bookRentalAppInstance.init();
    }
  });
})(); 